import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Loader2, CheckCircle, Sparkles, Target, Play } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const COMPANY_SIZES = [
  "1-10",
  "11-50",
  "51-200",
  "201-500",
  "501-1000",
  "1000+",
];

const AD_SPEND_OPTIONS = [
  "Less than $1,000",
  "$1,000 - $5,000",
  "$5,000 - $25,000",
  "$25,000 - $100,000",
  "$100,000+",
];

const HIGHLIGHTS = [
  { icon: Play, text: "Live product-to-video walkthrough" },
  { icon: Sparkles, text: "Custom template creation demo" },
  { icon: Target, text: "ROI & scaling strategies" },
];

export default function ContactUs() {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    company_name: "",
    job_title: "",
    work_email: "",
    company_size: "",
    monthly_ad_spend: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const isValid =
    form.first_name.trim() &&
    form.last_name.trim() &&
    form.company_name.trim() &&
    form.job_title.trim() &&
    form.work_email.trim() &&
    form.company_size &&
    form.monthly_ad_spend;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid) {
      toast.error("Please fill in all fields");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/api/contact`, form);
      setSubmitted(true);
      toast.success("Demo request submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: "#F5F5F0" }} data-testid="contact-page">
      {/* Header - matches Landing */}
      <header
        className="fixed top-0 left-0 right-0 z-50 border-b"
        style={{ backgroundColor: "rgba(245,245,240,0.85)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderColor: "rgba(0,0,0,0.06)" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="contact-logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_2d4aff0d-e5b7-449b-860f-09baa683a8bc/artifacts/b3uq1zlx_Logo.png"
              alt="Prysh AI"
              className="w-9 h-9 object-contain"
            />
            <span className="font-display text-lg font-semibold" style={{ color: "#1A1A1A" }}>Prysh AI</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium transition-colors"
            style={{ color: "rgba(26,26,26,0.5)" }}
            data-testid="contact-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 px-6 md:px-12 pt-28 pb-16 lg:pb-20">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left Column */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            className="lg:sticky lg:top-32"
          >
            <p
              className="text-xs font-bold uppercase tracking-[0.25em] mb-4"
              style={{ color: "#FF5A36" }}
            >
              Book a Demo — Get 1 Free Ad
            </p>
            <h1
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
              style={{ color: "#1A1A1A" }}
              data-testid="contact-headline"
            >
              See Prysh AI
              <br />
              in action
            </h1>
            <p className="mt-6 text-base lg:text-lg max-w-md leading-relaxed" style={{ color: "rgba(26,26,26,0.5)" }}>
              Book a demo and we'll create <span style={{ color: "#FF5A36", fontWeight: 600 }}>1 free ad</span> for you — see how brands and agencies create scroll-stopping video ads in minutes. AI-powered, tested, optimized, and ready to scale.
            </p>

            {/* Feature highlights */}
            <div className="mt-10 space-y-4">
              {HIGHLIGHTS.map(({ icon: Icon, text }, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, delay: 0.3 + i * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: "rgba(255,90,54,0.1)" }}
                  >
                    <Icon className="w-4 h-4" style={{ color: "#FF5A36" }} />
                  </div>
                  <span className="text-sm font-medium" style={{ color: "rgba(26,26,26,0.7)" }}>{text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Column — Form Card */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            {submitted ? (
              <div
                className="bg-white rounded-2xl p-10 text-center border"
                style={{ borderColor: "rgba(0,0,0,0.06)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}
                data-testid="contact-success"
              >
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="font-display text-2xl font-semibold mb-3" style={{ color: "#1A1A1A" }}>
                  Demo Request Received!
                </h2>
                <p className="mb-6" style={{ color: "rgba(26,26,26,0.5)" }}>
                  Thank you for your interest. Our team will reach out within 24
                  hours to schedule your demo.
                </p>
                <Link to="/">
                  <Button
                    variant="outline"
                    className="rounded-full px-6 border"
                    style={{ borderColor: "rgba(0,0,0,0.12)", color: "#1A1A1A" }}
                    data-testid="contact-back-btn"
                  >
                    Back to Home
                  </Button>
                </Link>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white rounded-2xl p-8 lg:p-10 space-y-5 border"
                style={{ borderColor: "rgba(0,0,0,0.06)", boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}
                data-testid="contact-form"
              >
                {/* Name Row */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium" style={{ color: "#1A1A1A" }}>
                      First Name<span className="ml-0.5" style={{ color: "#FF5A36" }}>*</span>
                    </Label>
                    <Input
                      value={form.first_name}
                      onChange={(e) => handleChange("first_name", e.target.value)}
                      className="h-11 bg-white rounded-xl"
                      style={{ borderColor: "rgba(0,0,0,0.1)" }}
                      data-testid="contact-first-name"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium" style={{ color: "#1A1A1A" }}>
                      Last Name<span className="ml-0.5" style={{ color: "#FF5A36" }}>*</span>
                    </Label>
                    <Input
                      value={form.last_name}
                      onChange={(e) => handleChange("last_name", e.target.value)}
                      className="h-11 bg-white rounded-xl"
                      style={{ borderColor: "rgba(0,0,0,0.1)" }}
                      data-testid="contact-last-name"
                    />
                  </div>
                </div>

                {/* Company Name */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: "#1A1A1A" }}>
                    Company Name<span className="ml-0.5" style={{ color: "#FF5A36" }}>*</span>
                  </Label>
                  <Input
                    value={form.company_name}
                    onChange={(e) => handleChange("company_name", e.target.value)}
                    className="h-11 bg-white rounded-xl"
                    style={{ borderColor: "rgba(0,0,0,0.1)" }}
                    data-testid="contact-company-name"
                  />
                </div>

                {/* Job Title */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: "#1A1A1A" }}>
                    Job Title<span className="ml-0.5" style={{ color: "#FF5A36" }}>*</span>
                  </Label>
                  <Input
                    value={form.job_title}
                    onChange={(e) => handleChange("job_title", e.target.value)}
                    className="h-11 bg-white rounded-xl"
                    style={{ borderColor: "rgba(0,0,0,0.1)" }}
                    data-testid="contact-job-title"
                  />
                </div>

                {/* Work Email */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: "#1A1A1A" }}>
                    Work Email<span className="ml-0.5" style={{ color: "#FF5A36" }}>*</span>
                  </Label>
                  <Input
                    type="email"
                    value={form.work_email}
                    onChange={(e) => handleChange("work_email", e.target.value)}
                    className="h-11 bg-white rounded-xl"
                    style={{ borderColor: "rgba(0,0,0,0.1)" }}
                    data-testid="contact-work-email"
                  />
                </div>

                {/* Company Size */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: "#1A1A1A" }}>
                    Company Size<span className="ml-0.5" style={{ color: "#FF5A36" }}>*</span>
                  </Label>
                  <Select
                    value={form.company_size}
                    onValueChange={(v) => handleChange("company_size", v)}
                  >
                    <SelectTrigger className="h-11 bg-white rounded-xl" style={{ borderColor: "rgba(0,0,0,0.1)" }} data-testid="contact-company-size">
                      <SelectValue placeholder="Please Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {COMPANY_SIZES.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s} employees
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Monthly Ad Spend */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium" style={{ color: "#1A1A1A" }}>
                    Monthly Ad Spend<span className="ml-0.5" style={{ color: "#FF5A36" }}>*</span>
                  </Label>
                  <Select
                    value={form.monthly_ad_spend}
                    onValueChange={(v) => handleChange("monthly_ad_spend", v)}
                  >
                    <SelectTrigger className="h-11 bg-white rounded-xl" style={{ borderColor: "rgba(0,0,0,0.1)" }} data-testid="contact-ad-spend">
                      <SelectValue placeholder="Please Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {AD_SPEND_OPTIONS.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Consent + Submit */}
                <p className="text-xs pt-1" style={{ color: "rgba(26,26,26,0.4)" }}>
                  By submitting this form you agree to be contacted by Prysh AI
                  about our products and services.
                </p>

                <Button
                  type="submit"
                  disabled={submitting || !isValid}
                  className="w-full h-12 rounded-full text-base font-medium border-0 transition-shadow hover:shadow-[0_8px_30px_rgba(255,90,54,0.25)]"
                  style={{ backgroundColor: "#FF5A36", color: "#FFFFFF" }}
                  data-testid="contact-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Book My Demo"
                  )}
                </Button>
              </form>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
}
